import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Alert,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Breadcrumbs,
    SelectChangeEvent,
    IconButton,
    Menu,
    Tooltip
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { Link as RouterLink } from 'react-router-dom';
import { useActiveMemberProfiles } from '../../hooks/useActiveMemberProfiles';
import { transactionAPI, paymentMethodAPI, memberProfileAPI, tenantSettingsAPI } from '../../services/api';
import { Transaction, TenantSettings } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { generateInvoiceHTML, openInvoiceWindow } from '../../utils/invoiceGenerator';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete } from '@mui/material';

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .no-rows-primary': {
        fill: '#3D4751',
        ...theme.applyStyles('light', {
            fill: '#AEB8C2',
        }),
    },
    '& .no-rows-secondary': {
        fill: '#1D2126',
        ...theme.applyStyles('light', {
            fill: '#E8EAED',
        }),
    },
}));

// Define status options
const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
    { value: 'cancelled', label: 'Cancelled' }
];

// Define type options
const typeOptions = [
    { value: 'membership_payment', label: 'Membership Payment' },
    { value: 'amenity_booking', label: 'Amenity Booking' },
    { value: 'class_booking', label: 'Class Booking' },
    { value: 'initiation_fee', label: 'Initiation Fee' },
    { value: 'guest_fee', label: 'Guest Fee' },
    { value: 'improvement_fee', label: 'Improvement Fee' },
    { value: 'coaching', label: 'Coaching' },
    { value: 'personal_training', label: 'Personal Training' },
    { value: 'personal_training_pack', label: 'Personal Training Pack' },
    { value: 'equipment_fee', label: 'Equipment Fee' },
    { value: 'add_on_payment', label: 'Add-On Payment' },
    { value: 'group_play_booking', label: 'Group Play Booking' },
    { value: 'refund', label: 'Refund' },
    { value: 'other', label: 'Other' }
];

const TransactionsPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Filter states
    const [searchText, setSearchText] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('pending'); // Default to pending
    const [typeFilter, setTypeFilter] = useState<string>('');
    
    // Date range filter states - default to last 30 days
    const [startDate, setStartDate] = useState<Date | null>(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date;
    });
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    // Sorting state
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'transactionDate', sort: 'desc' }]);

    // Pagination
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 100, // Limit to 100 transactions per page
    });
    const [totalTransactions, setTotalTransactions] = useState<number>(0);

    // Transaction detail dialog
    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [adminNote, setAdminNote] = useState<string>('');
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState<boolean>(false);
    const [processingAction, setProcessingAction] = useState<boolean>(false);
    const [processingTransaction, setProcessingTransaction] = useState<boolean>(false);
    const [processError, setProcessError] = useState<string | null>(null);
    const [processSuccess, setProcessSuccess] = useState<string | null>(null);

    // Action menu state
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuTransaction, setMenuTransaction] = useState<Transaction | null>(null);
    const [processDialogOpen, setProcessDialogOpen] = useState<boolean>(false);
    const [transactionToProcess, setTransactionToProcess] = useState<Transaction | null>(null);
    const [processingPayment, setProcessingPayment] = useState<boolean>(false);

    // Create transaction dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
    // Fetch member profiles for create dialog
    const { profiles: memberProfiles, loading: loadingMembers, refetch: refetchMemberProfiles } = useActiveMemberProfiles({ autoLoad: false });
    const [creatingTransaction, setCreatingTransaction] = useState<boolean>(false);
    const [createFormData, setCreateFormData] = useState<{
        memberProfileId: string;
        type: string;
        amount: number;
        tax: number;
        description: string;
        transactionDate: string;
        status: string;
        discountPercentage: number;
        discountAmount: number;
    }>({
        memberProfileId: '',
        type: 'other',
        amount: 0,
        tax: 0,
        description: '',
        transactionDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        discountPercentage: 0,
        discountAmount: 0
    });

    const taxDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Tenant settings for invoice customization
    const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);

    // Fetch tenant settings
    const fetchTenantSettings = async () => {
        try {
            const response = await tenantSettingsAPI.getAllSettings();
            setTenantSettings(response.data);
        } catch (err) {
            console.error('Error fetching tenant settings:', err);
            // Non-critical error, just log it
        }
    };

    // Fetch transactions
    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params: any = {
                status: statusFilter || undefined,
                type: typeFilter || undefined,
                search: searchText || undefined,
                limit: paginationModel.pageSize,
                offset: paginationModel.page * paginationModel.pageSize
            };

            // Apply sorting
            if (sortModel.length > 0) {
                params.sortBy = sortModel[0].field;
                params.sortOrder = sortModel[0].sort?.toUpperCase() || 'DESC';
            }

            // Apply date range - filter by transaction date
            if (startDate) {
                params.transactionStartDate = startDate.toISOString().split('T')[0];
            }
            if (endDate) {
                params.transactionEndDate = endDate.toISOString().split('T')[0];
            }

            const response = await transactionAPI.getAll(params);
            setTransactions(response.data.transactions);
            setTotalTransactions(response.data.total);
            setError(null);
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to load transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
        fetchTenantSettings();
    }, [paginationModel, statusFilter, typeFilter, sortModel, startDate, endDate]);

    // Handle search with debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (paginationModel.page === 0) {
                fetchTransactions();
            } else {
                // Reset to first page when search changes
                setPaginationModel({ ...paginationModel, page: 0 });
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    // Handle status filter change
    const handleStatusFilterChange = (e: SelectChangeEvent<string>) => {
        setStatusFilter(e.target.value);
        setPaginationModel({ ...paginationModel, page: 0 });
    };

    // Handle type filter change
    const handleTypeFilterChange = (e: SelectChangeEvent<string>) => {
        setTypeFilter(e.target.value);
        setPaginationModel({ ...paginationModel, page: 0 });
    };

    // Handle sort model change
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        setPaginationModel({ ...paginationModel, page: 0 });
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchText('');
        setStatusFilter('pending');
        setTypeFilter('');
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        setStartDate(thirtyDaysAgo);
        setEndDate(new Date());
        setSortModel([{ field: 'transactionDate', sort: 'desc' }]);
        setPaginationModel({ ...paginationModel, page: 0 });
    };

    // Get status color
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'error';
            case 'refunded': return 'info';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    // Format currency
    const formatCurrency = (amount: number | string | undefined) => {
        if (amount === undefined || amount === null) return '$0.00';
        const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericAmount);
    };

    // Format transaction type for display
    const formatTransactionType = (type: string) => {
        const option = typeOptions.find(opt => opt.value === type);
        return option?.label || type;
    };

    // Handle row click to open detail dialog
    const handleRowClick = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setAdminNote('');
        setProcessError(null);
        setProcessSuccess(null);
        setDetailDialogOpen(true);
    };

    // Handle close detail dialog
    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedTransaction(null);
        setAdminNote('');
        setProcessError(null);
        setProcessSuccess(null);
    };

    // Handle open cancel confirmation
    const handleOpenCancelConfirm = () => {
        if (!adminNote.trim()) {
            setError('Please provide a note explaining the cancellation reason.');
            return;
        }
        setCancelConfirmOpen(true);
    };

    // Handle cancel transaction
    const handleCancelTransaction = async () => {
        if (!selectedTransaction || !adminNote.trim()) {
            setError('Cannot cancel transaction: note is required.');
            return;
        }

        setProcessingAction(true);
        try {
            await transactionAPI.update(selectedTransaction.id, {
                status: 'cancelled',
                adminNote: adminNote.trim()
            });

            setSuccess('Transaction cancelled successfully.');
            setCancelConfirmOpen(false);
            setDetailDialogOpen(false);
            setSelectedTransaction(null);
            setAdminNote('');

            // Refresh transactions list
            fetchTransactions();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error cancelling transaction:', err);
            setError(err.response?.data?.message || 'Failed to cancel transaction. Please try again.');
            setCancelConfirmOpen(false);
        } finally {
            setProcessingAction(false);
        }
    };

    // Handle process transaction from modal
    const handleProcessTransactionFromModal = async () => {
        if (!selectedTransaction) return;

        setProcessingTransaction(true);
        setProcessError(null);
        setProcessSuccess(null);

        try {
            const response = await paymentMethodAPI.processTransaction(selectedTransaction.id);
            
            if (response.data.success) {
                const successMsg = `Payment processed successfully! ${
                    response.data.paymentSource === 'group' 
                        ? 'Charged to group: ' + response.data.billedToName 
                        : 'Charged to: ' + response.data.billedToName
                }`;
                setProcessSuccess(successMsg);
                
                // Fetch updated transaction details
                const updatedResponse = await transactionAPI.getAll({
                    status: undefined,
                    type: undefined,
                    search: selectedTransaction.id,
                    limit: 1,
                    offset: 0
                });

                if (updatedResponse.data.transactions.length > 0) {
                    setSelectedTransaction(updatedResponse.data.transactions[0]);
                }
                
                // Refresh transactions list
                fetchTransactions();
            } else {
                setProcessError(response.data.message || 'Payment processing failed');
            }
        } catch (err: any) {
            console.error('Error processing payment:', err);
            const errorMessage = err.response?.data?.message || 'Failed to process payment';
            setProcessError(errorMessage);
        } finally {
            setProcessingTransaction(false);
        }
    };

    // Handle action menu open
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuTransaction(transaction);
    };

    // Handle action menu close
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuTransaction(null);
    };

    // Handle process payment click
    const handleProcessPaymentClick = () => {
        handleMenuClose();
        if (menuTransaction) {
            setTransactionToProcess(menuTransaction);
            setProcessDialogOpen(true);
        }
    };

    // Handle create transaction dialog open
    const handleOpenCreateDialog = async () => {
        setCreateDialogOpen(true);
        
        // Load member profiles
        try {
            await refetchMemberProfiles();
        } catch (err) {
            console.error('Error loading member profiles:', err);
            setError('Failed to load member profiles');
        }
    };

    // Handle create transaction dialog close
    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setCreateFormData({
            memberProfileId: '',
            type: 'other',
            amount: 0,
            tax: 0,
            description: '',
            transactionDate: new Date().toISOString().split('T')[0],
            status: 'pending',
            discountPercentage: 0,
            discountAmount: 0
        });
    };

    // Handle create transaction
    const handleCreateTransaction = async () => {
        if (!createFormData.memberProfileId) {
            setError('Please select a member');
            return;
        }

        if (!createFormData.type) {
            setError('Please select a transaction type');
            return;
        }

        setCreatingTransaction(true);
        try {
            await transactionAPI.create({
                memberProfileId: createFormData.memberProfileId,
                type: createFormData.type,
                amount: createFormData.amount,
                tax: createFormData.tax,
                description: createFormData.description,
                transactionDate: createFormData.transactionDate,
                status: createFormData.status,
                discountPercentage: createFormData.discountPercentage,
                discountAmount: createFormData.discountAmount
            });

            setSuccess('Transaction created successfully');
            handleCloseCreateDialog();
            fetchTransactions();

            // Clear success message after 5 seconds
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            console.error('Error creating transaction:', err);
            setError(err.response?.data?.message || 'Failed to create transaction');
        } finally {
            setCreatingTransaction(false);
        }
    };

    // Handle get invoice click
    const handleGetInvoiceClick = async () => {
        handleMenuClose();
        if (!menuTransaction) return;

        try {
            const response = await paymentMethodAPI.getInvoice(menuTransaction.id);
            const invoiceData = response.data;

            // Get member profile for address
            const member = menuTransaction.memberProfile as any;
            const memberInfo = member ? {
                address: member.address,
                city: member.city,
                state: member.state,
                zipCode: member.zipCode,
                country: member.country
            } : undefined;

            // Generate invoice HTML using shared utility
            const invoiceHtml = generateInvoiceHTML({
                invoiceData,
                memberInfo,
                tenantSettings,
                transactionId: menuTransaction.id
            });

            // Open invoice in new window
            if (!openInvoiceWindow(invoiceHtml)) {
                setError('Please allow popups to view the invoice');
            }
        } catch (err: any) {
            console.error('Error getting invoice:', err);
            setError(err.response?.data?.message || 'Failed to get invoice');
        }
    };

    // Handle process payment confirm
    const handleProcessPayment = async () => {
        if (!transactionToProcess) return;

        setProcessingPayment(true);
        setError(null);
        try {
            const response = await paymentMethodAPI.processTransaction(transactionToProcess.id);
            
            if (response.data.success) {
                setSuccess(`Payment processed successfully! ${response.data.paymentSource === 'group' ? 'Charged to group: ' + response.data.billedToName : 'Charged to: ' + response.data.billedToName}`);
                setProcessDialogOpen(false);
                setTransactionToProcess(null);
                
                // Refresh transactions list
                fetchTransactions();
                
                // Clear success message after 5 seconds
                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            } else {
                setError(response.data.message || 'Payment processing failed');
            }
        } catch (err: any) {
            console.error('Error processing payment:', err);
            const errorMessage = err.response?.data?.message || 'Failed to process payment';
            setError(errorMessage);
            
            // If no payment method error, don't close dialog - let user see the message
            if (err.response?.data?.errorType !== 'no_payment_method') {
                setProcessDialogOpen(false);
                setTransactionToProcess(null);
            }
        } finally {
            setProcessingPayment(false);
        }
    };

    // Export to CSV
    const exportToCSV = async () => {
        if (transactions.length === 0) {
            setError('No transactions to export');
            return;
        }

        try {
            setLoading(true);
            
            // Fetch ALL transactions matching current filters (no pagination limit)
            const params: any = {
                status: statusFilter || undefined,
                type: typeFilter || undefined,
                search: searchText || undefined,
                limit: 10000, // Large limit to get all matching records
                offset: 0
            };

            // Apply sorting
            if (sortModel.length > 0) {
                params.sortBy = sortModel[0].field;
                params.sortOrder = sortModel[0].sort?.toUpperCase() || 'DESC';
            }

            // Apply date range
            if (startDate) {
                params.transactionStartDate = startDate.toISOString().split('T')[0];
            }
            if (endDate) {
                params.transactionEndDate = endDate.toISOString().split('T')[0];
            }

            const response = await transactionAPI.getAll(params);
            const allTransactions = response.data.transactions;

            if (allTransactions.length === 0) {
                setError('No transactions to export');
                setLoading(false);
                return;
            }

            // Helper function to escape and quote CSV fields
            const escapeCSVField = (field: any): string => {
                if (field === null || field === undefined) return '""';
                const stringField = String(field);
                // Escape quotes by doubling them and wrap in quotes
                const escapedField = stringField.replace(/"/g, '""');
                return `"${escapedField}"`;
            };

            // Define CSV headers
            const headers = [
                'Date',
                'Member',
                'Type',
                'Status',
                'Amount',
                'Discount',
                'Tax',
                'Total',
                'Billed To',
                'Payment Method',
                'Attempts',
                'Last Error',
                'Description'
            ];

            // Create CSV rows from all transactions
            const rows = allTransactions.map((transaction: Transaction) => {
                const member = transaction.memberProfile;
                const memberName = member ? `${member.firstName} ${member.lastName}` : 'Unknown';
                const billingInfo = getBillingInfo(transaction);
                const retryCount = transaction.retryCount || 0;
                const lastError = (transaction.metadata as any)?.lastError || '';
                const transactionDate = transaction.transactionDate ? formatDate(transaction.transactionDate) : formatDate(transaction.createdAt);

                return [
                    escapeCSVField(transactionDate),
                    escapeCSVField(memberName),
                    escapeCSVField(formatTransactionType(transaction.type)),
                    escapeCSVField(transaction.status?.replace('_', ' ').toUpperCase() || ''),
                    escapeCSVField(formatCurrency(transaction.amount)),
                    escapeCSVField(transaction.discountAmount && transaction.discountAmount > 0 ? `- ${formatCurrency(transaction.discountAmount)}` : '-'),
                    escapeCSVField(formatCurrency(transaction.tax || 0)),
                    escapeCSVField(formatCurrency(transaction.totalAmount)),
                    escapeCSVField(billingInfo.billedTo),
                    escapeCSVField(billingInfo.hasPaymentMethod ? (billingInfo.paymentSource === 'group' ? 'Group Card' : 'Personal Card') : 'No Payment Method'),
                    escapeCSVField(retryCount > 0 ? `${retryCount}/3` : '-'),
                    escapeCSVField(lastError),
                    escapeCSVField(transaction.description || '')
                ].join(',');
            });

            // Combine headers and rows
            const csvContent = [headers.map(h => escapeCSVField(h)).join(','), ...rows].join('\n');

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            
            // Generate filename with current date and filter info
            const dateStr = new Date().toISOString().split('T')[0];
            const filterInfo = [];
            if (statusFilter) filterInfo.push(statusFilter);
            if (typeFilter) filterInfo.push(typeFilter);
            const filename = `transactions_${dateStr}${filterInfo.length > 0 ? '_' + filterInfo.join('_') : ''}.csv`;
            
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setSuccess(`Exported ${allTransactions.length} transactions to CSV`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Error exporting to CSV:', err);
            setError('Failed to export transactions to CSV');
        } finally {
            setLoading(false);
        }
    };

    // Get billing info for transaction
    const getBillingInfo = (transaction: Transaction): { billedTo: string; hasPaymentMethod: boolean; paymentSource: string } => {
        const member = transaction.memberProfile;
        if (!member) {
            return { billedTo: 'Unknown', hasPaymentMethod: false, paymentSource: 'none' };
        }

        // Check if member has a group with group billing
        const hasGroupPayment = (member as any).memberGroup?.chargeToGroupPrimaryContact && 
                               (member as any).memberGroup?.paymentInfo;
        
        // Check for personal payment - handle both old and new structure
        const pmtInfo = (member as any).pmtProcessorInfo;
        const hasPersonalPayment = pmtInfo && (
            pmtInfo.customerId || // Old structure
            pmtInfo.helcim?.cards?.length > 0 || // New helcim structure
            pmtInfo.cards?.length > 0 // Legacy cards structure
        );

        if (hasGroupPayment) {
            return {
                billedTo: (member as any).memberGroup.groupName || 'Group',
                hasPaymentMethod: true,
                paymentSource: 'group'
            };
        } else if (hasPersonalPayment) {
            return {
                billedTo: `${member.firstName} ${member.lastName}`,
                hasPaymentMethod: true,
                paymentSource: 'personal'
            };
        } else {
            return {
                billedTo: `${member.firstName} ${member.lastName}`,
                hasPaymentMethod: false,
                paymentSource: 'none'
            };
        }
    };

    function CustomNoRowsOverlay() {
        return (
            <StyledGridOverlay>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    width={96}
                    viewBox="0 0 452 257"
                    aria-hidden
                    focusable="false"
                >
                    <path
                        className="no-rows-primary"
                        d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
                    />
                    <path
                        className="no-rows-primary"
                        d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
                    />
                    <path
                        className="no-rows-secondary"
                        d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
                    />
                </svg>
                <Box sx={{ mt: 2 }}>No transactions</Box>
            </StyledGridOverlay>
        );
    }


    // Define columns for DataGrid
    const columns: GridColDef[] = [
        {
            field: 'transactionDate',
            headerName: 'Date',
            width: 120,
            renderCell: (params: GridRenderCellParams) => {
                return params.value ? formatDate(params.value) : formatDate(params.row.createdAt);
            },
        },
        {
            field: 'memberProfile',
            headerName: 'Member',
            width: 220,
            renderCell: (params: GridRenderCellParams) => {
                const member = params.value;
                if (!member) return 'Unknown';
                return (
                    <RouterLink
                        to={`/member-profiles/${member.id}`}
                        style={{
                            textDecoration: 'none',
                            color: 'inherit',
                        }}
                    >
                        {`${member.firstName} ${member.lastName}`}
                    </RouterLink>
                );
            },
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 180,
            renderCell: (params: GridRenderCellParams) => formatTransactionType(params.value),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.value?.replace('_', ' ').toUpperCase() || ''}
                    size="small"
                    color={getStatusColor(params.value) as any}
                />
            ),
        },
        {
            field: 'amount',
            headerName: 'Amount',
            width: 110,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value),
        },
        {
            field: 'discountAmount',
            headerName: 'Discount',
            width: 110,
            renderCell: (params: GridRenderCellParams) => {
                const discount = params.value || 0;
                return discount > 0 ? `- ${formatCurrency(discount)}` : '-';
            },
        },
        {
            field: 'tax',
            headerName: 'Tax',
            width: 100,
            renderCell: (params: GridRenderCellParams) => formatCurrency(params.value || 0),
        },
        {
            field: 'totalAmount',
            headerName: 'Total',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <div style={{ fontWeight: 'bold' }}>{formatCurrency(params.value)}</div>
            ),
        },
        {
            field: 'billedTo',
            headerName: 'Billed To',
            width: 200,
            valueGetter: (value: any, row: Transaction) => {
                const billingInfo = getBillingInfo(row);
                return billingInfo.billedTo;
            },
            renderCell: (params: GridRenderCellParams) => {
                const billingInfo = getBillingInfo(params.row);
                return (
                    <Box>
                        <Typography variant="body2">{billingInfo.billedTo}</Typography>
                        {billingInfo.paymentSource === 'group' && (
                            <Chip label="Group Payment" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                        )}
                        {billingInfo.paymentSource === 'personal' && (
                            <Chip label="Personal" size="small" color="default" sx={{ height: 18, fontSize: '0.7rem' }} />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'paymentMethod',
            headerName: 'Payment Method',
            width: 150,
            valueGetter: (value: any, row: Transaction) => {
                const billingInfo = getBillingInfo(row);
                if (!billingInfo.hasPaymentMethod) {
                    return 'No Payment Method';
                }
                return billingInfo.paymentSource === 'group' ? 'Group Card' : 'Personal Card';
            },
            renderCell: (params: GridRenderCellParams) => {
                const billingInfo = getBillingInfo(params.row);
                if (!billingInfo.hasPaymentMethod) {
                    return <Typography variant="body2" color="error">No Payment Method</Typography>;
                }
                return (
                    <Typography variant="body2" color="success.main">
                        {billingInfo.paymentSource === 'group' ? 'Group Card' : 'Personal Card'}
                    </Typography>
                );
            },
        },
        {
            field: 'retryCount',
            headerName: 'Attempts',
            width: 100,
            renderCell: (params: GridRenderCellParams) => {
                const retryCount = params.value || 0;
                if (retryCount === 0) {
                    return <Typography variant="body2">-</Typography>;
                }
                const color = retryCount >= 3 ? 'error' : retryCount >= 2 ? 'warning' : 'default';
                return (
                    <Chip 
                        label={`${retryCount}/3`}
                        size="small"
                        color={color as any}
                    />
                );
            },
        },
        {
            field: 'lastError',
            headerName: 'Last Error',
            width: 250,
            valueGetter: (value: any, row: Transaction) => {
                return (row.metadata as any)?.lastError || '';
            },
            renderCell: (params: GridRenderCellParams) => {
                const lastError = (params.row.metadata as any)?.lastError;
                const isTerminalError = (params.row.metadata as any)?.isTerminalError;
                
                if (!lastError) {
                    return <Typography variant="body2" color="text.secondary">-</Typography>;
                }
                
                return (
                    <Tooltip title={lastError} arrow>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isTerminalError && (
                                <Chip 
                                    label="Terminal" 
                                    size="small" 
                                    color="error" 
                                    sx={{ height: 18, fontSize: '0.65rem', mr: 0.5 }} 
                                />
                            )}
                            <Typography 
                                variant="body2" 
                                color="error"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {lastError}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 80,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                const transaction = params.row;
                const canProcess = (transaction.status === 'pending' || transaction.status === 'failed') && 
                                   currentUser?.role === 'super_admin';
                const canGetInvoice = transaction.status === 'completed';
                
                if (!canProcess && !canGetInvoice) {
                    return null;
                }

                const tooltipTitle = canProcess ? 'Process Payment' : 'Get Invoice';

                return (
                    <Tooltip title={tooltipTitle}>
                        <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, transaction)}
                        >
                            <MoreVertIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <Typography color="text.primary">Transactions</Typography>
            </Breadcrumbs>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Transactions
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToCSV}
                        disabled={transactions.length === 0}
                    >
                        Export to CSV
                    </Button>
                    {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreateDialog}
                        >
                            Create Transaction
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Success message */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Main content */}
            <Paper sx={{ p: 3 }}>
                {/* Filters */}
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search by member name or email..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="small"
                            sx={{ minWidth: 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All</MenuItem>
                                {statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={typeFilter}
                                onChange={handleTypeFilterChange}
                                label="Type"
                            >
                                <MenuItem value="">All</MenuItem>
                                {typeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => {
                                    setStartDate(newValue);
                                    setPaginationModel({ ...paginationModel, page: 0 });
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { minWidth: 150 }
                                    }
                                }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => {
                                    setEndDate(newValue);
                                    setPaginationModel({ ...paginationModel, page: 0 });
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: { minWidth: 150 }
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <Button
                            variant="outlined"
                            onClick={clearFilters}
                            startIcon={<ClearIcon />}
                            size="small"
                        >
                            Clear Filters
                        </Button>
                    </Box>
                </Stack>

                {loading && transactions.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: 700, width: '100%' }}>
                        <DataGrid
                            rows={transactions}
                            columns={columns}
                            getRowId={(row) => row.id}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[100]}
                            rowCount={totalTransactions}
                            paginationMode="server"
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            loading={loading}
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                            onRowClick={(params) => handleRowClick(params.row as Transaction)}
                            slots={{
                                noRowsOverlay: CustomNoRowsOverlay,
                                noResultsOverlay: CustomNoRowsOverlay,
                            }}
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'linear-progress',
                                    noRowsVariant: 'skeleton',
                                },
                            }}
                            sx={{
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                },
                            }}

                        />
                    </Box>
                )}
            </Paper>

            {/* Transaction Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Transaction Details</DialogTitle>
                <DialogContent>
                    {selectedTransaction && (
                        <Box sx={{ mt: 2 }}>
                            {/* Success Alert */}
                            {processSuccess && (
                                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProcessSuccess(null)}>
                                    {processSuccess}
                                </Alert>
                            )}

                            {/* Error Alert */}
                            {processError && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProcessError(null)}>
                                    {processError}
                                </Alert>
                            )}

                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Transaction ID</Typography>
                                    <Typography variant="body1">{selectedTransaction.id}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Member</Typography>
                                    <Typography variant="body1">
                                        {selectedTransaction.memberProfile
                                            ? `${selectedTransaction.memberProfile.firstName} ${selectedTransaction.memberProfile.lastName}`
                                            : 'Unknown'}
                                    </Typography>
                                    {selectedTransaction.memberProfile && (
                                        <Typography variant="body2" color="text.secondary">
                                            {selectedTransaction.memberProfile.email}
                                        </Typography>
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                    <Typography variant="body1">{formatTransactionType(selectedTransaction.type)}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                    <Chip
                                        label={selectedTransaction.status.replace('_', ' ').toUpperCase()}
                                        size="small"
                                        color={getStatusColor(selectedTransaction.status) as any}
                                    />
                                </Box>

                                 {(selectedTransaction.metadata as any)?.lastError && (
                                    <Box sx={{ bgcolor: 'white', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {(selectedTransaction.metadata as any)?.isTerminalError ? 'Terminal Error (No Retry)' : 'Last Processing Error'} on {formatDate((selectedTransaction.metadata as any).lastErrorAt)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {(selectedTransaction.metadata as any)?.lastError}
                                        </Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Amount Breakdown</Typography>
                                    <Typography variant="body1">Amount: {formatCurrency(selectedTransaction.amount)}</Typography>
                                    {selectedTransaction.discountAmount > 0 && (
                                        <Typography variant="body1" color="success.main">
                                            Discount: -{formatCurrency(selectedTransaction.discountAmount)} ({selectedTransaction.discountPercentage}%)
                                        </Typography>
                                    )}
                                    <Typography variant="body1">Tax: {formatCurrency(selectedTransaction.tax)}</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        Total: {formatCurrency(selectedTransaction.totalAmount)}
                                    </Typography>
                                </Box>

                                {selectedTransaction.description && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                                        <Typography variant="body1">{selectedTransaction.description}</Typography>
                                    </Box>
                                )}

                                {selectedTransaction.transactionDate && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Transaction Date</Typography>
                                        <Typography variant="body1">{formatDate(selectedTransaction.transactionDate)}</Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                                    <Typography variant="body1">{formatDate(selectedTransaction.createdAt)}</Typography>
                                </Box>

                                {(selectedTransaction.retryCount ?? 0) > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">Retry Attempts</Typography>
                                        <Typography variant="body1">{selectedTransaction.retryCount}</Typography>
                                     
                                    </Box>
                                )}

                                {selectedTransaction.metadata?.adminCancellation && (
                                    <Box sx={{ bgcolor: 'error.light', p: 2, borderRadius: 1 }}>
                                        <Typography variant="subtitle2" color="error.dark">Administrative Cancellation</Typography>
                                        <Typography variant="body2" color="error.dark">
                                            {selectedTransaction.metadata.adminCancellationNote}
                                        </Typography>
                                        <Typography variant="caption" color="error.dark">
                                            Cancelled at: {selectedTransaction.metadata.cancelledAt && formatDate(selectedTransaction.metadata.cancelledAt)}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Process Transaction Button (Super Admin Only) */}
                                {currentUser?.role === 'super_admin' && 
                                 (selectedTransaction.status === 'pending' || selectedTransaction.status === 'failed') && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>Process Transaction</Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Manually process this transaction using the configured payment method.
                                            The payment will be charged immediately.
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleProcessTransactionFromModal}
                                            disabled={processingTransaction}
                                            startIcon={processingTransaction ? <CircularProgress size={20} /> : null}
                                            sx={{ mt: 2 }}
                                        >
                                            {processingTransaction ? 'Processing...' : 'Process Transaction'}
                                        </Button>
                                    </Box>
                                )}

                                {selectedTransaction.status === 'pending' && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom>Cancel Transaction</Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            To cancel this pending transaction, please provide a note explaining the reason.
                                            This action cannot be undone.
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Cancellation Note (Required)"
                                            value={adminNote}
                                            onChange={(e) => setAdminNote(e.target.value)}
                                            placeholder="Enter the reason for cancelling this transaction..."
                                            sx={{ mt: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={handleOpenCancelConfirm}
                                            disabled={!adminNote.trim() || processingAction}
                                            sx={{ mt: 2 }}
                                        >
                                            Cancel Transaction
                                        </Button>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetailDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={cancelConfirmOpen}
                onClose={() => !processingAction && setCancelConfirmOpen(false)}
            >
                <DialogTitle>Confirm Transaction Cancellation</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to cancel this transaction?
                    </Typography>
                    <Typography variant="body2" color="error" fontWeight="bold">
                        This action cannot be undone.
                    </Typography>
                    {adminNote && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="subtitle2">Cancellation Note:</Typography>
                            <Typography variant="body2">{adminNote}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelConfirmOpen(false)} disabled={processingAction}>
                        No, Keep Transaction
                    </Button>
                    <Button
                        onClick={handleCancelTransaction}
                        color="error"
                        variant="contained"
                        disabled={processingAction}
                    >
                        {processingAction ? <CircularProgress size={24} /> : 'Yes, Cancel Transaction'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {menuTransaction?.status === 'pending' || menuTransaction?.status === 'failed' ? (
                    <MenuItem onClick={handleProcessPaymentClick}>
                        <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
                        Process Payment
                    </MenuItem>
                ) : null}
                {menuTransaction?.status === 'completed' ? (
                    <MenuItem onClick={handleGetInvoiceClick}>
                        <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                        Get Invoice
                    </MenuItem>
                ) : null}
            </Menu>

            {/* Process Payment Confirmation Dialog */}
            <Dialog
                open={processDialogOpen}
                onClose={() => !processingPayment && setProcessDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Process Payment</DialogTitle>
                <DialogContent>
                    {transactionToProcess && (
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <Alert severity="warning">
                                This will charge the payment method immediately.
                            </Alert>
                            
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Member</Typography>
                                <Typography variant="body1">
                                    {transactionToProcess.memberProfile?.firstName} {transactionToProcess.memberProfile?.lastName}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Transaction Type</Typography>
                                <Typography variant="body1">{formatTransactionType(transactionToProcess.type)}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                                <Typography variant="body1" fontWeight="bold">
                                    {formatCurrency(transactionToProcess.totalAmount)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Billed To</Typography>
                                <Typography variant="body1">
                                    {getBillingInfo(transactionToProcess).billedTo}
                                    {getBillingInfo(transactionToProcess).paymentSource === 'group' && (
                                        <Chip label="Group Payment" size="small" color="primary" sx={{ ml: 1, height: 20 }} />
                                    )}
                                    {getBillingInfo(transactionToProcess).paymentSource === 'personal' && (
                                        <Chip label="Personal Payment" size="small" color="default" sx={{ ml: 1, height: 20 }} />
                                    )}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">Payment Method</Typography>
                                {getBillingInfo(transactionToProcess).hasPaymentMethod ? (
                                    <Typography variant="body1" color="success.main">
                                        ✓ {getBillingInfo(transactionToProcess).paymentSource === 'group' ? 'Group Card on File' : 'Personal Card on File'}
                                    </Typography>
                                ) : (
                                    <Typography variant="body1" color="error">
                                        ✗ No Payment Method Defined
                                    </Typography>
                                )}
                            </Box>

                            {!getBillingInfo(transactionToProcess).hasPaymentMethod && (
                                <Alert severity="error">
                                    Cannot process payment: No payment method is defined for this member or their group.
                                </Alert>
                            )}

                            {error && (
                                <Alert severity="error" onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setProcessDialogOpen(false);
                            setTransactionToProcess(null);
                            setError(null);
                        }} 
                        disabled={processingPayment}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProcessPayment}
                        color="primary"
                        variant="contained"
                        disabled={processingPayment || !transactionToProcess || !getBillingInfo(transactionToProcess).hasPaymentMethod}
                        startIcon={processingPayment ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                    >
                        {processingPayment ? 'Processing...' : 'Process Payment Now'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Transaction Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => !creatingTransaction && handleCloseCreateDialog()}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create Transaction</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Alert severity="info">
                            Create a manual transaction for a member. The transaction will be created with the specified details.
                        </Alert>

                        {/* Member Selection */}
                        <Autocomplete
                            options={memberProfiles}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} - ${option.email}`}
                            loading={loadingMembers}
                            value={memberProfiles.find(m => m.id === createFormData.memberProfileId) || null}
                            onChange={(event, newValue) => {
                                setCreateFormData({
                                    ...createFormData,
                                    memberProfileId: newValue?.id || ''
                                });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Member *"
                                    placeholder="Search for a member..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingMembers ? <CircularProgress size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {/* Transaction Type */}
                        <FormControl fullWidth>
                            <InputLabel>Transaction Type *</InputLabel>
                            <Select
                                value={createFormData.type}
                                onChange={(e) => setCreateFormData({ ...createFormData, type: e.target.value })}
                                label="Transaction Type *"
                            >
                                {typeOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Description */}
                        <TextField
                            fullWidth
                            label="Description"
                            value={createFormData.description}
                            onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                            placeholder="Optional description for this transaction"
                            multiline
                            rows={2}
                        />

                        {/* Amount */}
                        <TextField
                            fullWidth
                            label="Amount *"
                            type="number"
                            value={createFormData.amount}
                            onChange={(e) => {
                                const amt = parseFloat(e.target.value) || 0;
                                setCreateFormData(prev => ({ ...prev, amount: amt }));
                                if (taxDebounceRef.current) clearTimeout(taxDebounceRef.current);
                                taxDebounceRef.current = setTimeout(() => {
                                    setCreateFormData(prev => ({ ...prev, tax: parseFloat((amt * 0.13).toFixed(2)) }));
                                }, 500);
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                        />

                        {/* Tax */}
                        <TextField
                            fullWidth
                            label="Tax (13%)"
                            type="number"
                            value={createFormData.tax}
                            onChange={(e) => setCreateFormData({ ...createFormData, tax: parseFloat(e.target.value) || 0 })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                            helperText="Auto-calculated at 13% — edit to override"
                        />

                        {/* Discount Percentage */}
                        <TextField
                            fullWidth
                            label="Discount Percentage"
                            type="number"
                            value={createFormData.discountPercentage}
                            onChange={(e) => setCreateFormData({ ...createFormData, discountPercentage: parseFloat(e.target.value) || 0 })}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                            inputProps={{ step: 0.01, min: 0, max: 100 }}
                        />

                        {/* Discount Amount */}
                        <TextField
                            fullWidth
                            label="Discount Amount"
                            type="number"
                            value={createFormData.discountAmount}
                            onChange={(e) => setCreateFormData({ ...createFormData, discountAmount: parseFloat(e.target.value) || 0 })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                        />

                        {/* Transaction Date */}
                        <TextField
                            fullWidth
                            label="Transaction Date"
                            type="date"
                            value={createFormData.transactionDate}
                            onChange={(e) => setCreateFormData({ ...createFormData, transactionDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Status */}
                        <FormControl fullWidth>
                            <InputLabel>Status *</InputLabel>
                            <Select
                                value={createFormData.status}
                                onChange={(e) => setCreateFormData({ ...createFormData, status: e.target.value })}
                                label="Status *"
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Total Preview */}
                        <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Transaction Summary</Typography>
                            <Stack spacing={1}>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Amount:</Typography>
                                    <Typography variant="body2">${createFormData.amount.toFixed(2)}</Typography>
                                </Box>
                                {createFormData.discountPercentage > 0 && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">Discount ({createFormData.discountPercentage}%):</Typography>
                                        <Typography variant="body2" color="error">
                                            -${(createFormData.amount * createFormData.discountPercentage / 100).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                                {createFormData.discountAmount > 0 && (
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">Fixed Discount:</Typography>
                                        <Typography variant="body2" color="error">-${createFormData.discountAmount.toFixed(2)}</Typography>
                                    </Box>
                                )}
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Tax:</Typography>
                                    <Typography variant="body2">${createFormData.tax.toFixed(2)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" sx={{ borderTop: 1, borderColor: 'divider', pt: 1 }}>
                                    <Typography variant="body1" fontWeight="bold">Total:</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        ${(
                                            createFormData.amount 
                                            - (createFormData.amount * createFormData.discountPercentage / 100) 
                                            - createFormData.discountAmount 
                                            + createFormData.tax
                                        ).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateDialog} disabled={creatingTransaction}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateTransaction}
                        color="primary"
                        variant="contained"
                        disabled={creatingTransaction || !createFormData.memberProfileId || !createFormData.type}
                        startIcon={creatingTransaction ? <CircularProgress size={20} /> : null}
                    >
                        {creatingTransaction ? 'Creating...' : 'Create Transaction'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TransactionsPage;
